import { Component, computed, inject, input } from '@angular/core';
import { RouterModule } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import * as fromSeriousSystem from '@serious-system';
import * as fromGenerated from '../../_generated';
import * as fromAuth from '../../auth';
import * as fromFavorites from '../../favorites';
import * as fromAssistants from '../assistants';
import { RouterService } from '../shared/services/router.service';

@Component({
  imports: [
    RouterModule,
    TranslateModule,
    fromAssistants.AssistantLogoComponent,
    fromSeriousSystem.UseUIBasicIconDirective,
    fromSeriousSystem.UseArrowIconDirective,
  ],
  selector: 'squadbox-teammate-assistant-card',
  template: `
    <div
      role="button"
      tabindex="0"
      class="
        group
        bg-shades-white border-neutral-200 border rounded-xl
        h-full p-6 flex-1
        flex flex-col gap-4
        cursor-pointer tablet-landscape:hover:shadow-md transition-shadow duration-200
      "
      (click)="handleClickCardCTA(cardData().uuid)"
      (keydown.enter)="handleClickCardCTA(cardData().uuid)"
      (keydown.space)="
        handleClickCardCTA(cardData().uuid); $event.preventDefault()
      "
    >
      <!--header-->
      <div class="flex justify-between">
        <div class="bg-primary-50 rounded-lg size-12 p-3">
          <squadbox-assistant-logo
            class="
              [&>svg,img]:text-primary-500/50
              [&>svg,img]:size-6
              [&>svg,img]:shrink-0
            "
            [logo]="cardData().logo"
          />
        </div>

        <button class="size-5" (click)="handleClickToggleFavoriteCTA($event)">
          @if (isFavorite()) {
            <svg sdUseUIBasicIcon="star-filled" class="text-primary-500"></svg>
          } @else {
            <svg sdUseUIBasicIcon="star" class="text-neutral-400"></svg>
          }
        </button>
      </div>
      <!--content-->
      <div class="grow flex flex-col gap-3">
        <p class="typo-p1 line-clamp-2 wrap-anywhere">
          {{ cardData().name }}
        </p>
        <p class="typo-p3 text-neutral-500 h-[4lh] line-clamp-4 wrap-anywhere">
          {{ cardData().description }}
        </p>
      </div>
      <!--footer-->
      <div
        class="
          flex items-end justify-between gap-4 w-full
        "
      >
        @if (shouldShowOwnerLabel()) {
          <p
            class="typo-caption font-semibold bg-primary-50 px-2 py-1 rounded w-fit text-primary-700"
          >
            @if (isSelfOwnedNonSquadBoxAssistant()) {
              {{ 'ASSISTANTS.CARD.OWNER_LABEL_ME' | translate }}
            } @else {
              {{
                'ASSISTANTS.CARD.OWNER_LABEL'
                  | translate: { ownerLabel: cardData().ownerLabel }
              }}
            }
          </p>
        }
        <div class="card-arrow-action ml-auto">
          <svg sdUseArrowIcon="arrow-right"></svg>
        </div>
      </div>
    </div>
  `,
})
export class TeammateAssistantComponent {
  private readonly routerService = inject(RouterService);
  private readonly authStore = inject(Store<fromAuth.AuthState>);
  private readonly favoritesStore = inject(
    Store<fromFavorites.AssistantFavoritesState>,
  );

  public readonly cardData = input.required<fromGenerated.AssistantView>();
  private readonly currentUser = this.authStore.selectSignal(
    fromAuth.authFeature.selectUser,
  );
  public readonly isSelfOwnedNonSquadBoxAssistant = computed(() => {
    const ownedBy = this.cardData().ownedBy;
    const currentUser = this.currentUser();

    return (
      !!currentUser &&
      ownedBy === currentUser.id &&
      currentUser.role !== 'super_admin'
    );
  });
  public readonly shouldShowOwnerLabel = computed(
    () =>
      this.isSelfOwnedNonSquadBoxAssistant() || !!this.cardData().ownerLabel,
  );

  public readonly favoriteEntities = this.favoritesStore.selectSignal(
    fromFavorites.assistantFavoritesFeature.selectEntities,
  );

  public readonly isFavorite = computed(
    () => this.cardData().uuid in this.favoriteEntities(),
  );

  public async handleClickCardCTA(uuid: string) {
    await this.routerService.navigateToEmptyConversationWithAssistant(uuid);
  }

  public handleClickToggleFavoriteCTA(event: MouseEvent) {
    event.stopPropagation();

    const assistantId = this.cardData().uuid;
    if (this.isFavorite()) {
      this.favoritesStore.dispatch(
        fromFavorites.AssistantFavoritesActions.removeFavorite({
          assistantId,
        }),
      );
    } else {
      this.favoritesStore.dispatch(
        fromFavorites.AssistantFavoritesActions.addFavorite({ assistantId }),
      );
    }
  }
}
